import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Phone, Building2, Link2, Package, FileText, ExternalLink } from "lucide-react";

function Row({ icon: Icon, label, children, copy }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-b-0">
      <div className="w-9 h-9 shrink-0 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#CCFF00]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-mono-jb text-zinc-500">
          {label}
        </div>
        <div className="text-sm mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

export default function LeadDetailDialog({ lead, onClose }) {
  if (!lead) return null;

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="lead-detail-dialog"
        className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">
            {lead.name}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] font-mono-jb uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#CCFF00]/10 text-[#CCFF00]">
              {lead.status}
            </span>
            <span className="text-xs text-zinc-500 font-mono-jb">
              {new Date(lead.created_at).toLocaleString()}
            </span>
          </div>
        </DialogHeader>

        <div className="mt-4 grid sm:grid-cols-[1fr_120px] gap-4">
          <div>
            <Row icon={Mail} label="Email">
              <a
                href={`mailto:${lead.email}`}
                className="text-[#CCFF00] hover:underline"
              >
                {lead.email}
              </a>
            </Row>
            <Row icon={Phone} label="Phone">
              {lead.phone ? (
                <a href={`tel:${lead.phone}`} className="text-[#CCFF00] hover:underline">
                  {lead.phone}
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row icon={Building2} label="Business">
              {lead.business_name || "—"}
            </Row>
            <Row icon={Package} label="Selected Packages">
              <div className="flex flex-wrap gap-2">
                {lead.selected_build_package && (
                  <span className="px-2 py-1 rounded-full bg-zinc-800 text-xs">
                    Build: {lead.selected_build_package}
                  </span>
                )}
                {lead.selected_maintenance_package && (
                  <span className="px-2 py-1 rounded-full bg-zinc-800 text-xs">
                    Maintenance: {lead.selected_maintenance_package}
                  </span>
                )}
                {!lead.selected_build_package && !lead.selected_maintenance_package && (
                  <span>{lead.interested_package || "—"}</span>
                )}
              </div>
            </Row>
            <Row icon={Link2} label="Reference Website">
              {lead.reference_website ? (
                <a
                  href={lead.reference_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#CCFF00] hover:underline inline-flex items-center gap-1"
                >
                  {lead.reference_website}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row icon={Link2} label="Current Website">
              {lead.current_website ? (
                <a
                  href={lead.current_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#CCFF00] hover:underline inline-flex items-center gap-1"
                >
                  {lead.current_website}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row icon={Link2} label="Sample Website">
              {lead.sample_website ? (
                <a
                  href={lead.sample_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#CCFF00] hover:underline inline-flex items-center gap-1"
                >
                  {lead.sample_website}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                "—"
              )}
            </Row>
          </div>

          {/* Logo */}
          {lead.logo_data_url ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono-jb text-zinc-500 mb-2">
                Logo
              </div>
              <div className="rounded-xl border border-[#27272A] bg-black/40 p-2 flex items-center justify-center">
                <img
                  src={lead.logo_data_url}
                  alt="Customer logo"
                  className="max-w-full max-h-32 object-contain"
                />
              </div>
              <a
                href={lead.logo_data_url}
                download={`${(lead.business_name || lead.name || "logo").replace(/[^a-z0-9]/gi, "_")}_logo`}
                className="mt-2 text-xs text-[#CCFF00] hover:underline inline-flex items-center gap-1 font-mono-jb"
                data-testid="lead-detail-logo-download"
              >
                Download <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : null}
        </div>

        {lead.project_details && (
          <div className="mt-2">
            <div className="flex items-start gap-3 py-3">
              <div className="w-9 h-9 shrink-0 rounded-lg bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#CCFF00]" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-mono-jb text-zinc-500">
                  Project Details
                </div>
                <p className="text-sm mt-1 text-zinc-200 whitespace-pre-wrap">
                  {lead.project_details}
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
